import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"

/**
 * Pure helpers for expanding association nodes in a discovered schema. A to-one
 * association ("jobOrder") and a to-many list ("commissions[]") each arrive as an
 * expandable node carrying its associatedEntityType; expanding one re-roots that
 * entity's own schema under the node's path ("jobOrder.onSite", "commissions[].role").
 * To-many fields nested inside the expanded entity are left out — vendors (Bullhorn)
 * return to-many fields at the top level only.
 */

export function isExpandableNode(field: AtsFieldDescriptor): boolean {
  return Boolean(field.isExpandable && field.associatedEntityType)
}

/** The associated entity's fields, re-rooted under the node path */
export function expandNodeFields(nodePath: string, associatedFields: AtsFieldDescriptor[]): AtsFieldDescriptor[] {
  return associatedFields
    .filter((field) => field.kind !== "ToMany")
    .map((field) => ({ ...field, path: `${nodePath}.${field.path}` }))
}

/** Appends expanded fields right after their node, skipping paths already present */
export function mergeExpandedFields(
  fields: AtsFieldDescriptor[],
  nodePath: string,
  expanded: AtsFieldDescriptor[]
): AtsFieldDescriptor[] {
  const known = new Set(fields.map((field) => field.path))
  const additions = expanded.filter((field) => !known.has(field.path))
  const nodeIndex = fields.findIndex((field) => field.path === nodePath)

  if (nodeIndex < 0) {
    return [...fields, ...additions]
  }

  // Keep the node's own leaves (e.g. "jobOrder.id") together with the expansion
  let insertAt = nodeIndex + 1
  while (insertAt < fields.length && fields[insertAt].path.startsWith(`${nodePath}.`)) {
    insertAt++
  }

  return [...fields.slice(0, insertAt), ...additions, ...fields.slice(insertAt)]
}

/** Whether a path addresses the items of a to-many association ("commissions[].role") */
function isToManyItemPath(path: string): boolean {
  return path.includes("[].")
}

/**
 * Paths a pull can turn into columns: scalars, and the leaves of a to-many association,
 * which the pull lays out per the chosen to-many mode. Association nodes and bare lists
 * ("skills[]") are not columns.
 */
export function isColumnField(field: AtsFieldDescriptor): boolean {
  if (isExpandableNode(field) || field.dataType === "object" || field.dataType === "array") return false
  return !field.isArray || isToManyItemPath(field.path)
}

/**
 * Association nodes that still need expanding for every selected path to appear in the
 * list: for "jobOrder.clientCorporation.name" that is "jobOrder", then (once it exists)
 * "jobOrder.clientCorporation"; for "commissions[].role" it is the list node
 * "commissions[]". Nodes are returned outermost first, one per prefix.
 */
export function nodesToExpandFor(selectedPaths: readonly string[], fields: readonly AtsFieldDescriptor[] | null, expandedPaths: ReadonlySet<string>): string[] {
  if (!fields) return []
  const known = new Set(fields.map((field) => field.path))
  const expandable = new Set(fields.filter(isExpandableNode).map((field) => field.path))
  const pending = new Set<string>()
  for (const path of selectedPaths) {
    if (known.has(path)) continue
    const segments = path.split(".")
    for (let depth = 1; depth < segments.length; depth++) {
      const prefix = segments.slice(0, depth).join(".")
      if (expandable.has(prefix) && !expandedPaths.has(prefix)) {
        pending.add(prefix)
        break
      }
    }
  }
  return Array.from(pending)
}
